import React from "react";
import { motion } from "framer-motion";
import { palette } from "../palette";
import { GrainDefs, GrainOverlay } from "./Grain";
import { useReducedMotion } from "../motion";

const W = 320;
const H = 200;
const c = palette.ill;

// Head-and-shoulders, seen from behind or three-quarters, so the figure reads
// as "a person like you" rather than a specific stock photo of someone.
const Bust = ({ x, y, shirt, skin, hair, scale = 1, longHair }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`}>
    {longHair && <rect x="-24" y="-30" width="48" height="52" rx="21" fill={hair} />}
    <path d="M-30 46 Q-30 8 0 8 Q30 8 30 46 Z" fill={shirt} />
    <circle cx="0" cy="-16" r="23" fill={hair} />
    <circle cx="0" cy="-10" r="20" fill={skin} />
  </g>
);

const SCENES = {
  // Data analyst: dashboards behind, climbing toward the model underneath.
  scratch: ({ reduced }) => (
    <>
      <rect width={W} height={H} fill={c.sky} />
      <rect x="26" y="26" width="120" height="76" rx="10" fill={palette.paper} />
      <rect x="40" y="76" width="16" height="16" rx="3" fill={c.skyDeep} />
      <rect x="62" y="62" width="16" height="30" rx="3" fill={c.river} />
      <rect x="84" y="48" width="16" height="44" rx="3" fill={c.plum} />
      <rect x="106" y="58" width="16" height="34" rx="3" fill={c.leaf} />
      <rect x="40" y="38" width="46" height="6" rx="3" fill={c.sand} />

      <motion.g
        {...(reduced ? {} : {
          animate: { y: [0, -6, 0] },
          transition: { duration: 5, repeat: Infinity, ease: "easeInOut" },
        })}
      >
        <rect x="174" y="34" width="118" height="60" rx="10" fill={palette.paper} />
        <path d="M188 78 L214 60 L238 68 L278 46" stroke={c.river} strokeWidth="4.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="278" cy="46" r="5" fill={c.clay} />
      </motion.g>

      <rect y={H - 46} width={W} height="46" fill={c.sand} />
      <Bust x={160} y={H - 58} shirt={c.river} skin={c.skin[3]} hair={c.ink} scale={1.15} />
    </>
  ),

  // Software engineer: code on one side, deployed service on the other.
  engineer: ({ reduced }) => (
    <>
      <rect width={W} height={H} fill={c.sand} />
      <circle cx="160" cy="92" r="82" fill={c.sky} opacity="0.8" />
      <rect x="22" y="44" width="104" height="80" rx="9" fill={c.ink} />
      {[0, 1, 2, 3].map((i) => (
        <rect key={i} x="34" y={58 + i * 15} width={[54, 38, 62, 30][i]} height="6" rx="3"
          fill={[c.leaf, c.river, c.skyDeep, c.sun][i]} />
      ))}
      <motion.g
        {...(reduced ? {} : {
          animate: { y: [0, -7, 0] },
          transition: { duration: 4.6, repeat: Infinity, ease: "easeInOut" },
        })}
      >
        <rect x="196" y="40" width="98" height="64" rx="10" fill={palette.paper} />
        <circle cx="222" cy="72" r="11" fill={c.leaf} />
        <rect x="242" y="60" width="38" height="6" rx="3" fill={c.skyDeep} />
        <rect x="242" y="74" width="28" height="6" rx="3" fill={c.skyDeep} />
      </motion.g>
      <rect y={H - 44} width={W} height="44" fill={c.leafDeep} opacity="0.3" />
      <Bust x={160} y={H - 56} shirt={c.leafDeep} skin={c.skin[0]} hair="#3a2b26" scale={1.15} longHair />
    </>
  ),

  // Founder: pitching a rising chart, with reach going out to an audience.
  founder: ({ reduced }) => (
    <>
      <rect width={W} height={H} fill={c.sky} />
      <rect x="90" y="24" width="150" height="94" rx="10" fill={palette.paper} stroke={c.ink} strokeWidth="4" />
      <path d="M106 100 L136 76 L162 86 L190 52 L226 38" stroke={c.river} strokeWidth="5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <motion.circle
        cx="226" cy="38" r="7" fill={c.clay}
        {...(reduced ? {} : {
          animate: { scale: [1, 1.25, 1] },
          transition: { duration: 2.6, repeat: Infinity, ease: "easeInOut" },
        })}
        style={{ transformOrigin: "226px 38px" }}
      />
      <rect x="150" y="118" width="30" height="26" fill={c.woodDeep} />
      <rect y={H - 40} width={W} height="40" fill={c.sand} />

      {/* Presenter */}
      <g>
        <path d="M32 112 Q32 92 52 92 Q72 92 72 112 L72 170 L32 170 Z" fill={c.plum} />
        <path d="M70 118 Q96 106 108 84" stroke={c.skin[2]} strokeWidth="10" strokeLinecap="round" fill="none" />
        <circle cx="52" cy="76" r="18" fill={c.skin[2]} />
        <circle cx="52" cy="70" r="19" fill={c.ink} />
        <circle cx="52" cy="78" r="16" fill={c.skin[2]} />
      </g>
      {/* Audience */}
      {[228, 274].map((x, i) => (
        <g key={x}>
          <path d={`M${x - 20} ${H} Q${x - 20} ${H - 34} ${x} ${H - 34} Q${x + 20} ${H - 34} ${x + 20} ${H} Z`} fill={i ? c.clay : c.leafDeep} />
          <circle cx={x} cy={H - 46} r="15" fill={c.skin[i ? 1 : 3]} />
          <circle cx={x} cy={H - 50} r="16" fill={c.ink} opacity="0.85" />
          <circle cx={x} cy={H - 43} r="13" fill={c.skin[i ? 1 : 3]} />
        </g>
      ))}
    </>
  ),

  // Manager: in front of a team and a plan, not a terminal. Whiteboard with
  // roadmap, budget and org boxes; people around the table.
  manager: ({ reduced }) => (
    <>
      <rect width={W} height={H} fill={c.sand} />
      <rect x="18" y="18" width="200" height="112" rx="8" fill={palette.paper} stroke={c.woodDeep} strokeWidth="4" />

      {/* Roadmap swimlanes */}
      {[0, 1, 2].map((i) => (
        <g key={i}>
          <rect x="32" y={38 + i * 24} width="26" height="8" rx="4" fill={c.skyDeep} opacity="0.7" />
          <motion.rect
            x="64" y={38 + i * 24} width={[86, 56, 108][i]} height="8" rx="4"
            fill={[c.river, c.leaf, c.plum][i]}
            {...(reduced ? {} : {
              animate: { opacity: [0.55, 1, 0.55] },
              transition: { duration: 3 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 },
            })}
          />
        </g>
      ))}
      {/* Budget bars */}
      <rect x="32" y="112" width="14" height="10" rx="2" fill={c.sun} />
      <rect x="52" y="106" width="14" height="16" rx="2" fill={c.sun} />
      <rect x="72" y="100" width="14" height="22" rx="2" fill={c.clay} />
      <rect x="104" y="106" width="60" height="5" rx="2.5" fill={c.sand} />
      <rect x="104" y="116" width="42" height="5" rx="2.5" fill={c.sand} />

      {/* Presenter, pointing at the board */}
      <g>
        <path d="M236 200 Q236 146 264 146 Q292 146 292 200 Z" fill={c.plum} />
        <path d="M240 152 Q214 138 202 112" stroke={c.skin[1]} strokeWidth="10" strokeLinecap="round" fill="none" />
        <circle cx="264" cy="124" r="21" fill={c.skin[1]} />
        <circle cx="264" cy="118" r="22" fill={c.ink} />
        <circle cx="264" cy="127" r="19" fill={c.skin[1]} />
      </g>

      {/* Team at the table */}
      <rect y="168" width="210" height="7" rx="3" fill={c.woodDeep} />
      {[44, 104, 164].map((x, i) => (
        <g key={x}>
          <path d={`M${x - 21} 200 Q${x - 21} 172 ${x} 172 Q${x + 21} 172 ${x + 21} 200 Z`} fill={[c.leafDeep, c.river, c.clay][i]} />
          <circle cx={x} cy="156" r="15" fill={c.skin[i]} />
          <circle cx={x} cy="152" r="16" fill={c.ink} opacity="0.85" />
          <circle cx={x} cy="159" r="13" fill={c.skin[i]} />
        </g>
      ))}
    </>
  ),

  // Specialist: someone at the machine late, writing kernels. Dark on purpose —
  // it is the one plan that lives underneath everything else.
  niche: ({ reduced }) => (
    <>
      <rect width={W} height={H} fill="#14181f" />
      {/* Monitor glow spilling into the room */}
      <ellipse cx="206" cy="86" rx="118" ry="86" fill={c.river} opacity="0.14" />

      {/* GPU tower, left */}
      <rect x="16" y="82" width="46" height="96" rx="7" fill="#242a34" />
      {[0, 1, 2, 3].map((i) => (
        <g key={i}>
          <rect x="24" y={92 + i * 22} width="30" height="13" rx="3" fill="#323a47" />
          <motion.circle
            cx="31" cy={98.5 + i * 22} r="3" fill={i % 2 ? c.leaf : c.sun}
            {...(reduced ? {} : {
              animate: { opacity: [0.25, 1, 0.25] },
              transition: { duration: 1.7 + i * 0.4, repeat: Infinity },
            })}
          />
        </g>
      ))}

      {/* Monitor */}
      <rect x="132" y="24" width="164" height="104" rx="8" fill="#0e1218" stroke="#39414f" strokeWidth="3" />
      {/* left half: kernel source */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <rect key={i} x="146" y={40 + i * 13} width={[44, 30, 52, 38, 24, 46][i]} height="5" rx="2.5"
          fill={[c.leaf, c.river, "#6b7686", c.sun, c.river, "#6b7686"][i]} opacity="0.95" />
      ))}
      {/* right half: a thread-block grid, lighting up as it executes */}
      {Array.from({ length: 16 }).map((_, i) => {
        const col = i % 4;
        const row = Math.floor(i / 4);
        return (
          <motion.rect
            key={i}
            x={214 + col * 17} y={44 + row * 17} width="12" height="12" rx="2.5"
            fill={c.river}
            {...(reduced
              ? { opacity: 0.55 }
              : {
                  animate: { opacity: [0.18, 0.95, 0.18] },
                  transition: {
                    duration: 2.2,
                    repeat: Infinity,
                    delay: (col + row) * 0.16,
                    ease: "easeInOut",
                  },
                })}
          />
        );
      })}
      <rect x="206" y="124" width="18" height="12" fill="#39414f" />
      <rect x="188" y="134" width="54" height="5" rx="2.5" fill="#39414f" />

      {/* Desk */}
      <rect y="152" width={W} height="6" fill="#39414f" />
      <rect y="158" width={W} height={H - 158} fill="#1b202a" />

      {/* The person, from behind, rim-lit by the screen */}
      <g>
        <path d="M64 200 Q64 150 106 150 Q148 150 148 200 Z" fill="#2b3340" />
        <path d="M106 150 Q148 150 148 200 L136 200 Q136 158 106 156 Z" fill={c.river} opacity="0.5" />
        <circle cx="106" cy="126" r="25" fill="#2b3340" />
        <path d="M106 101 a25 25 0 0 1 25 25 a25 25 0 0 1 -10 20 q6 -22 -15 -45 z" fill={c.river} opacity="0.55" />
        {/* headphones */}
        <path d="M81 124 a25 25 0 0 1 50 0" stroke="#4b5566" strokeWidth="5" fill="none" strokeLinecap="round" />
        <rect x="76" y="120" width="10" height="17" rx="4" fill="#4b5566" />
        <rect x="126" y="120" width="10" height="17" rx="4" fill="#4b5566" />
      </g>

      {/* Mug, because this is a 2am activity */}
      <rect x="258" y="138" width="17" height="15" rx="3" fill={c.clay} />
      <path d="M275 142 q7 3 0 7" stroke={c.clay} strokeWidth="3" fill="none" />
      {!reduced && (
        <motion.path
          d="M266 134 q5 -8 0 -15"
          stroke="#6b7686" strokeWidth="2.5" fill="none" strokeLinecap="round"
          animate={{ opacity: [0.15, 0.5, 0.15], y: [0, -5, 0] }}
          transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
        />
      )}
    </>
  ),
};

export const PersonaScene = ({ kind, className }) => {
  const reduced = useReducedMotion();
  const Scene = SCENES[kind] || SCENES.scratch;
  const id = `persona-${kind}`;

  return (
    <motion.svg
      className={className}
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      role="img"
      aria-label="An illustration of this kind of candidate and the work their plan focuses on"
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
      key={kind}
      initial={reduced ? false : { opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
    >
      <defs>
        <GrainDefs id={id} />
        <clipPath id={`${id}-clip`}>
          <rect width={W} height={H} rx="18" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${id}-clip)`}>
        <Scene reduced={reduced} />
        <GrainOverlay id={id} width={W} height={H} rx={18} />
      </g>
    </motion.svg>
  );
};

export default PersonaScene;
